import { useEffect, useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DEFAULT_FILTERS, FILE_SIZE_BAND_LABELS } from '../documents.constants'
import { DocumentsFilters, FileSizeBand } from '../documents.types'

type DocumentsFiltersSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: DocumentsFilters
  onApply: (filters: DocumentsFilters) => void
}

// No document-type or owner filter — no server param for either. Organisation/source stay
// free-text since there's no distinct-values endpoint for them. Tags has one (the `tags`
// facet), so it's a dropdown on the toolbar instead (tag-facet-selector.tsx), not here.
export const DocumentsFiltersSheet = ({
  open,
  onOpenChange,
  filters,
  onApply,
}: DocumentsFiltersSheetProps) => {
  const [draft, setDraft] = useState(filters)

  useEffect(() => {
    if (open) setDraft(filters)
  }, [open, filters])

  const update = <K extends keyof DocumentsFilters>(key: K, value: DocumentsFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-5 mt-4">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="georeferenced-only">Only show georeferenced</Label>
            <Switch
              id="georeferenced-only"
              checked={draft.georeferencedOnly}
              onCheckedChange={(checked) => update('georeferencedOnly', checked)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Date uploaded</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={draft.uploadedFrom ?? ''}
                onChange={(e) => update('uploadedFrom', e.target.value || null)}
                className="border border-border"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={draft.uploadedTo ?? ''}
                onChange={(e) => update('uploadedTo', e.target.value || null)}
                className="border border-border"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Date published</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={draft.publishedFrom ?? ''}
                onChange={(e) => update('publishedFrom', e.target.value || null)}
                className="border border-border"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={draft.publishedTo ?? ''}
                onChange={(e) => update('publishedTo', e.target.value || null)}
                className="border border-border"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>File size</Label>
            <Select
              value={draft.fileSizeBand}
              onValueChange={(value) => update('fileSizeBand', value as FileSizeBand)}
            >
              <SelectTrigger className="border border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FILE_SIZE_BAND_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Organisation</Label>
            <Input
              placeholder="e.g. Christchurch City Council"
              value={draft.organisation}
              onChange={(e) => update('organisation', e.target.value)}
              className="border border-border"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Permissions</Label>
            <Select
              value={draft.visibility}
              onValueChange={(value) =>
                update('visibility', value as DocumentsFilters['visibility'])
              }
            >
              <SelectTrigger className="border border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="organisation">Organisation</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Source</Label>
            <Input
              placeholder="e.g. Field survey"
              value={draft.source}
              onChange={(e) => update('source', e.target.value)}
              className="border border-border"
            />
          </div>
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="ghost"
            onClick={() => setDraft({ ...DEFAULT_FILTERS, search: draft.search })}
          >
            Reset
          </Button>
          <Button
            onClick={() => {
              onApply(draft)
              onOpenChange(false)
            }}
          >
            Apply filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
